// EncuestasListadoUC.tsx
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import useLists from 'utils/customHooks/useLists';

interface EncuestasCListadoUCProps {
  filters: any;
}

type TableData = (string | number)[][];

const commentHeaders: string[] = ['Nombre UC', 'Comentarios'];

const customHeaders: string[] = [
  'Curso',
  'El tiempo del curso alcanzó para desarrollar bien los temas de la unidad curricular',
  'En esta unidad curricular abordé temas que entiendo serán parte de mi futuro desempeño profesional',
  'El manejo de tecnología específica en esta unidad curricular fue satisfactorio',
  'Para mi esta UC fue... (dificultad)',
  'Total Respuestas',
  'Total Evaluadas'
];

const buildDataGridRows = (data: TableData) =>
  data.slice(1)
    .filter(row => row.join('').toLowerCase() !== customHeaders.join('').toLowerCase())
    .map((row, index) => ({
      id: index + 1,
      values: row
    }))
    .sort((a, b) => String(a.values[0]).localeCompare(String(b.values[0])));

const buildCommentRows = (data: TableData) => {
  const agrupados: { [docente: string]: Set<string> } = {};
  data.slice(1).forEach(row => {
    const docente = String(row[0] || '').trim().replace(/(.+?)\1+/, '$1');
    const comentario = String(row[1] || '').trim();
    const comentariosSeparados = comentario
      .split(/\.(?!\d)/)
      .map(c => c.trim())
      .filter(Boolean);
    if (!agrupados[docente]) agrupados[docente] = new Set();
    comentariosSeparados.forEach(texto => {
      const limpio = texto.toLowerCase().includes('pajera')
        ? '• Comentario omitido por agravio.'
        : `• ${texto}.`;
      agrupados[docente].add(limpio);
    });
  });
  return Object.entries(agrupados)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([docente, comentarios], index) => ({
      id: index + 1,
      docente,
      comentarios: Array.from(comentarios)
    }));
};

const downloadCSV = (rows: any[][], headers: string[], filename: string) => {
  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

const EncuestasCListadoUC: React.FC<EncuestasCListadoUCProps> = ({ filters }) => {
    // Asegurar que 'filters' sea siempre un array
  const safeFilters = Array.isArray(filters) ? filters : [];
  const [loadingSurveys, setLoadingSurveys] = useState(true);
  const [visibleComments, setVisibleComments] = useState<string | null>(null);

  const { users: answersUC1S, isLoadingList: loading1 } = useLists('/v1.1/encuestascoordinador/listadoUC1S', filters);
  const { users: answersUC2S, isLoadingList: loading2 } = useLists('/v1.1/encuestascoordinador/listadoUC2S', filters);
  const { users: commentsUC1S } = useLists('/v1.1/encuestascoordinador/comentariosUC1S', filters);
  const { users: commentsUC2S } = useLists('/v1.1/encuestascoordinador/comentariosUC2S', filters);

  useEffect(() => {
    setLoadingSurveys(loading1 || loading2);
  }, [loading1, loading2]);

  // Extraer 'year' usando safeFilters
  const year = safeFilters.find((f: any) => f.codigo === 'year')?.valor;
  if (year === 2025) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No hay datos para los filtros seleccionados</p>
        <p>Por favor, seleccioná un año distinto a {year}.</p>
      </div>
    );
  }

  const data1 = answersUC1S as unknown as TableData;
  const data2 = answersUC2S as unknown as TableData;
  const comm1 = commentsUC1S as unknown as TableData;
  const comm2 = commentsUC2S as unknown as TableData;
  
  const encuestas = [];
  
// Primero 2S
if ((Array.isArray(data2) && data2.length > 1) || (Array.isArray(comm2) && comm2.length > 1)) {
  encuestas.push({
    id: '2S',
    title: 'Encuestas UC 2S',
    data: data2,
    comments: comm2,
    csv: 'uc2s.csv',
    commentCsv: 'comentarios2s.csv'
  });
}
  
// Después 1S
if (Array.isArray(data1) && data1.length > 1) {
  encuestas.push({
    id: '1S',
    title: (data2.length <= 1 && comm2.length <= 1) ? 'Encuestas Anuales' : 'Encuestas UC 1S',
    data: data1,
    comments: comm1,
    csv: (data2.length <= 1 && comm2.length <= 1) ? 'uc_anual.csv' : 'uc1s.csv',
    commentCsv: (data2.length <= 1 && comm2.length <= 1) ? 'comentarios_anual.csv' : 'comentarios1s.csv'
  });
}


  
  return (
    <div style={{ padding: '2rem' }}>
      {loadingSurveys ? (
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>Cargando encuestas UC…</div>
      ) : (
        encuestas.map(({ id, title, data, comments, csv, commentCsv }) => {
          if (!Array.isArray(data) || data.length < 2) return null;
          const rows = buildDataGridRows(data);
          const commentRows = buildCommentRows(comments);
          return (
            <div key={id} style={{ marginBottom: '3rem' }}>
              <h2 style={{ textAlign: 'center' }}>{title}</h2>
              <div style={{ overflow: 'auto', maxHeight: 600 }}>
                <table style={{ minWidth: 1000, borderCollapse: 'collapse', width: '100%' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#f1f1f1', zIndex: 1 }}>
                    <tr>
                      {customHeaders.map((h, i) => (
                        <th key={i} style={{ border: '1px solid #ddd', padding: 8, textAlign: 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={idx} style={{ background: idx % 2 ? '#f9f9f9' : '#fff' }}>
                        {r.values.map((val, i) => (
                          <td key={i} style={{ border: '1px solid #ddd', padding: 8 }}>{val}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                <button
                  onClick={() => setVisibleComments(id)}
                  style={{ padding: '0.5rem 1rem', background: '#00AEEF', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                  Ver Comentarios
                </button>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button onClick={() => {
                    const csvRows = rows.map(r => r.values);
                    downloadCSV(csvRows, customHeaders, csv);
                  }}
                    style={{ padding: '0.5rem 1rem', background: '#00AEEF', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                    Exportar Encuestas
                  </button>
                  <button onClick={() => {
                    const csvRows = commentRows.map(c => [c.docente, ...c.comentarios]);
                    downloadCSV(csvRows, commentHeaders, commentCsv);
                  }}
                    style={{ padding: '0.5rem 1rem', background: '#00AEEF', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                    Exportar Comentarios
                  </button>
                </div>
              </div>
              {visibleComments === id && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
                  <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, width: '64%', maxHeight: '64%', overflowY: 'auto' }}>
                    <h3>Comentarios {title}</h3>
                    {commentRows.map(c => (
                      <div key={c.id} style={{ marginBottom: '1rem' }}>
                        <strong>{c.docente}</strong>
                        <ul style={{ marginTop: 4 }}>
                          {c.comentarios.map((text, i) => (
                            <li key={i}>{text}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    <div style={{ textAlign: 'right' }}>
                      <button onClick={() => setVisibleComments(null)} style={{ padding: '0.5rem 1rem', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                        Cerrar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({ filters: state.filters });

export default connect(mapStateToProps)(EncuestasCListadoUC);
